/**
 * Locale switcher utility for runtime locale management
 * This module provides functions to get and set the active locale.
 */
import { configureLocalization } from '@lit/localize';
import { LOCALE_CODES, AVAILABLE_LOCALES, DEFAULT_LOCALE } from './locale-codes.js';
// Templates will be dynamically loaded for each locale
import * as frTemplates from './fr.js';
// Additional module templates (registered by consuming applications)
const moduleTemplateLoaders = [];
/**
 * Register additional template loaders from consuming modules
 * This allows modules like flow to register their translations
 * Call this BEFORE the first setLocale() call
 */
export function registerModuleTemplates(loader) {
    moduleTemplateLoaders.push(loader);
}
// Configure the localization system
const { getLocale: litGetLocale, setLocale: litSetLocale } = configureLocalization({
    sourceLocale: LOCALE_CODES.ENGLISH,
    targetLocales: [LOCALE_CODES.FRENCH],
    loadLocale: async (locale) => {
        // Load French templates
        if (locale === LOCALE_CODES.FRENCH) {
            // Start with ui-components templates
            const mergedTemplates = { ...frTemplates.templates };
            // Load and merge templates from registered modules
            for (const loader of moduleTemplateLoaders) {
                try {
                    const moduleTemplates = await loader();
                    Object.assign(mergedTemplates, moduleTemplates.templates);
                }
                catch (error) {
                    console.warn('Failed to load module templates:', error);
                }
            }
            return { templates: mergedTemplates };
        }
        // Source locale (English) doesn't need templates
        throw new Error(`Unknown locale: ${locale}`);
    },
});
/**
 * Set the active locale for all localized components
 * @param locale - The locale code to activate
 * @throws Error if locale is not supported
 */
export async function setLocale(locale) {
    if (!AVAILABLE_LOCALES.includes(locale)) {
        throw new Error(`Unsupported locale "${locale}". Available locales: ${AVAILABLE_LOCALES.join(', ')}`);
    }
    await litSetLocale(locale);
}
/**
 * Get the currently active locale
 * @returns The current locale code
 */
export function getLocale() {
    return litGetLocale();
}
// Export locale constants for convenience
export { LOCALE_CODES, AVAILABLE_LOCALES, DEFAULT_LOCALE };
//# sourceMappingURL=locale-switcher.js.map